/**
 * useMasterPostsForTopic — real-time subscription to master + derivative
 * posts for a single topic, sourced from `projects/{pid}/master_posts/*`.
 *
 * Returns separate `master` (the platform-agnostic source) and
 * `derivatives` (per-platform adaptations linking back via sourcePostId).
 *
 * Phase D scope note: this hook covers the standalone master_posts path
 * (no strategy attached). When strategy support arrives, swap to a
 * collectionGroup query against `posts` so both flat and nested posts
 * surface together.
 */

import { useEffect, useState, useMemo } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

export interface MasterOrDerivativePost {
  id: string;
  topicId: string;
  sourcePostId: string | null;
  platform: string | null;
  hook?: string;
  body?: string;
  cta?: string;
  hashtags?: string[];
  voiceMatchScore?: number;
  status?: string;
  createdAt?: any;
  updatedAt?: any;
  [key: string]: any;
}

export interface UseMasterPostsForTopicReturn {
  master: MasterOrDerivativePost | null;
  derivatives: MasterOrDerivativePost[];
  posts: MasterOrDerivativePost[]; // raw list (master + derivatives)
  loading: boolean;
  error: Error | null;
}

export function useMasterPostsForTopic(
  projectId: string | null,
  topicId: string | null,
): UseMasterPostsForTopicReturn {
  const [posts, setPosts] = useState<MasterOrDerivativePost[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId && topicId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !topicId) {
      setPosts([]);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const q = query(
      collection(db, 'projects', projectId, 'master_posts'),
      where('topicId', '==', topicId),
    );
    setLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MasterOrDerivativePost));
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, topicId]);

  const { master, derivatives } = useMemo(() => {
    const m = posts.find((p) => p.sourcePostId == null && p.platform == null) ?? null;
    const d = posts.filter((p) => p.sourcePostId != null);
    return { master: m, derivatives: d };
  }, [posts]);

  return { master, derivatives, posts, loading, error };
}
